import InvoiceInformationRequest from 'components/InvoiceInformationRequest';
import InvoiceDisputeOffice from 'components/InvoiceDisputeOffice';
import InvoicePaymentAudit, { officeInvoiceIsPaid } from 'components/InvoicePaymentAudit';
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router";
import TopBarRedesigned from "components/TopBarRedesigned/TopBarRedesigned";
import {getPaymentTerms, formatTalentDisplayName} from "utils";
import { formatBillableDuration } from 'utils/billableDuration';
import { HygienistProps, InvoiceProps, InvoicePayNowQuote, JobProps, DateString, TimeString } from "utils/types";
import { BasicContainer, StyledCheckboxInput, AddressSection, DateSection, BillingSection, BillingRow, SectionTitle, PaymentInfo, PaymentSection, PaymentNote, ButtonsContainer, PayNowButton, AlreadyPaidButton, AlreadyPaidBanner, MarkAsPaidSection, PaymentStatusBanner } from "./InvoicePage.styles";
import dayjs from "dayjs";
import useActionApi from "../../../hooks/useActionApi";
import Api from "../../../api";
import {Analytics} from "utils/analytics";
import FixedLoader from "../../../components/FixedLoader";
import {SimpleModal} from "../../../components/Modal";
import {InvoiceAlreadyPaidModal} from "./components/AlreadyPaidModal";
import {PayInvoiceModal} from "./components/PayInvoiceModal";
import { ErrorModal } from "./components/ErrorModal";
import { CalendarTodayOutlined, AccessTimeOutlined, PaymentOutlined, HelpOutlineOutlined, CheckCircleOutline } from "@mui/icons-material";
import { CrossButtonBlack } from "components/Buttons/CrossButton";
import { StyledButton } from "components/Buttons/DefaultButton/DefaultButton.styles";
import clsx from "clsx";
import styles from "../PostJobPage/PostJob.module.scss";
import { CHAT_POST_SHIFT_NOTE, shouldShowPostShiftChatNote } from 'utils/shiftChat';
import { getTalentPaymentStatusCopy, TalentPaymentStatus } from './paymentStatusCopy';
import { formatInvoiceDuration } from 'utils/invoiceDuration';
import { formatInvoiceCents, getInvoicePayNowQuote, sameInvoicePayNowQuote } from 'utils/invoicePayNowQuote';
import useInvoiceDisputeActor from 'hooks/useInvoiceDisputeActor';


const convertDentalOfficeStatus = (status: number) => {
    if (status === 1) {
        return 'Pro'
    }
    if (status === 2) {
        return 'Elite'
    }
    return 'Active'
}

export interface JobWithInvoiceProps extends JobProps {
    hygienist: HygienistProps,
    invoice: InvoiceProps
}

type PayInvoiceResponse = {
    paymentLifecycleId: number;
    officePaymentStatus: 'paid';
    talentPaymentStatus: TalentPaymentStatus;
    talentPaymentMethod: 'instant' | 'standard' | null;
};

// @ts-ignore
const app = window?.app as { hidePreloader: () => void } | undefined;

type InvoiceRouteState = { jobId?: number; job?: JobWithInvoiceProps };

function invoiceRouteJobId(state: InvoiceRouteState | undefined): number {
    const id = Number(state?.jobId ?? state?.job?.id);
    return Number.isSafeInteger(id) && id > 0 ? id : 0;
}

const InvoicePage = () => {
    const history = useHistory();
    const location = useLocation<InvoiceRouteState | undefined>();
    const { actor, currentActor } = useInvoiceDisputeActor('office');
    const jobId = invoiceRouteJobId(location.state);
    if (!actor || !jobId) return null;

    // Notifications can replace only route state while this page stays mounted.
    // A fresh visit drops private data, consent, dialogs and pending callbacks.
    const visit = `${actor}:${location.key || ''}:${location.pathname}:${jobId}`;
    const isVisitCurrent = () => currentActor() === actor
        && history.location.key === location.key
        && history.location.pathname === location.pathname
        && invoiceRouteJobId(history.location.state as InvoiceRouteState | undefined) === jobId;
    return <InvoiceVisit key={visit} jobId={jobId} jobData={location.state?.job} isVisitCurrent={isVisitCurrent} />;
};

const InvoiceVisit = ({ jobId, jobData, isVisitCurrent }: {
    jobId: number; jobData?: JobWithInvoiceProps; isVisitCurrent: () => boolean;
}) => {
    app?.hidePreloader();
    const history = useHistory();
    const mounted = useRef(true);
    const readVersion = useRef(0);
    useLayoutEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; readVersion.current += 1; };
    }, []);
    const isCurrent = () => mounted.current && isVisitCurrent();
    const [loading, setLoading] = useState(true)

    const [checked, setChecked] = useState(false)
    const [job, setJob] = useState<JobWithInvoiceProps | undefined>(jobData)
    const [error, setError] = useState<string>('')
    const [errorTitle, setErrorTitle] = useState<string>('Error')
    const [alreadyPaidModal, setAlreadyPaidModal] = useState<boolean>(false)
    const [alreadyPaid, setAlreadyPaid] = useState<boolean>(false)
    const [payInvoiceModal, setPayInvoiceModal] = useState<boolean>(false)
    const [contactSupportOpen, setContactSupportOpen] = useState<boolean>(false)
    const [quoteNotice, setQuoteNotice] = useState('');
    const [paymentConfirmationPending, setPaymentConfirmationPending] = useState(false);
    const [talentPaymentStatus, setTalentPaymentStatus] = useState<TalentPaymentStatus | null>(
        jobData?.invoice?.talentPaymentStatus || null,
    )

    const isUSA = job?.location?.country === 1;
    const talentHasStripeExpress = !!job?.hygienist?.stripeCreatedTimestamp;
    const isPayNowAvailable = !isUSA || talentHasStripeExpress;

    const quote = job ? getInvoicePayNowQuote(job.invoice) : null;
    const invoiceAmount = quote ? formatInvoiceCents(quote.baseAmountCents) : '';
    const invoiceAmtWithFee = quote ? formatInvoiceCents(quote.officeAmountCents) : '';

    const goHome = () => {
        if (!isCurrent()) return;
        const fromInvoices = (history.location.state as any)?.fromDentistInvoices;
        if (fromInvoices) {
            history.push({ pathname: 'dentistInvoices' });
            return;
        }
        // Default: return to React dentist home instead of legacy home
        history.push({ pathname: 'dentistHome' });
    }

    const [getInvoiceForJobLoading, , getInvoiceForJob] = useActionApi({
        apiMethod: (id: number) => {
            if (!isCurrent()) return Promise.reject(new Error('Invoice visit changed'));
            return Api.getInvoiceForJob(id);
        },
        onSuccess: (invoice: InvoiceProps) => {
            if (!isCurrent() || !job) {
                return;
            }
            setJob(current => current ? { ...current, invoice } : current);
            setAlreadyPaid(officeInvoiceIsPaid(invoice));
            setTalentPaymentStatus(invoice.talentPaymentStatus || null);
            if (officeInvoiceIsPaid(invoice)) return;
            const refreshedQuote = getInvoicePayNowQuote(invoice);
            if (!checked || !sameInvoicePayNowQuote(quote, refreshedQuote)) {
                setChecked(false);
                setQuoteNotice('The payment total changed. Please review it and authorize payment again.');
                return;
            }
            payInvoice({ invoiceId: invoice.id, expectedQuote: quote! });
        },
        onError: (e: any) => {
            if (!isCurrent()) return;
            setLoading(false);
            setErrorTitle('Failed to load invoice');
            setError('Could not retrieve invoice details. Please try again later.');
        }
    });

    const getData = async () => {
        if (!isCurrent()) return;
        const version = ++readVersion.current;
        const isReadCurrent = () => isCurrent() && readVersion.current === version;
        try {
            const fetchedJob = jobData?.id === jobId ? jobData : await Api.getJobDetails(jobId);
            if (!isReadCurrent()) return;
            const invoice = await Api.getInvoiceForJob(jobId);
            if (!isReadCurrent()) return;
            // Job details can include a partial invoice; the invoice endpoint has
            // the authoritative lunch/downtime breakdown for this screen.
            setJob({...fetchedJob, invoice})
            setChecked(false);
            setAlreadyPaid(officeInvoiceIsPaid(invoice));
            setTalentPaymentStatus(invoice.talentPaymentStatus || null);
            if (officeInvoiceIsPaid(invoice)) setPaymentConfirmationPending(false);
        } catch (e) {
            if (!isReadCurrent()) return;
            setErrorTitle('Failed to load job details');
            setError('Could not retrieve job details. Please try again later.');
        } finally {
            if (isReadCurrent()) setLoading(false);
        }
    }

    useEffect(() => {
        getData();
        // InvoiceVisit is keyed by the exact actor, route visit and invoice job.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [payInvoiceLoading, , payInvoice] = useActionApi<{ invoiceId: number; expectedQuote: InvoicePayNowQuote }, PayInvoiceResponse>({
        apiMethod: ({ invoiceId, expectedQuote }) => {
            if (!isCurrent()) return Promise.reject(new Error('Invoice visit changed'));
            return Api.payInvoice(invoiceId, expectedQuote);
        },
        onSuccess: (payment) => {
            if(!isCurrent() || !job) {
                return;
            }
            setAlreadyPaid(true)
            setPaymentConfirmationPending(false);
            setTalentPaymentStatus(payment.talentPaymentStatus);
            setJob(current => current ? {
                ...current,
                invoice: {
                    ...current.invoice,
                    dentistMarkedPaid: 1,
                    creditPaid: 1,
                    paymentLifecycleId: payment.paymentLifecycleId,
                    officePaymentStatus: payment.officePaymentStatus,
                    talentPaymentStatus: payment.talentPaymentStatus,
                    talentPaymentMethod: payment.talentPaymentMethod,
                },
            } : current);
            Analytics.track('pay invoice', {
                shiftStartDate: new Date(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].actualStart).toISOString(),
                shiftEndDate: new Date(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].actualEnd).toISOString(),
                shiftId: job.shifts[0].id,
                totalInvoiceAmount: job.invoice.totalInvoiceAmt,
                currencyCode: job.location.country ? 'USD' : 'CAD',
                dentalWorkerStatus: convertDentalOfficeStatus(job.location?.status ? job.location?.status : 0),
                dentalWorkerType: job.hygienist.type ? 'Assistant' : 'Hygienist',
                hasUploadedResume: !!(job.hygienist
                    && job.hygienist.resumeUrl
                    && job.hygienist.resumeUrl.length),
                graduationYear: job.hygienist.graduationYear + '-01-01',
                dentalOfficeLocationId: job.locationId,
                shiftPostDate: new Date(job.postedOn).toISOString(),
            });
        },
        onError: (e: any) => {
            if (!isCurrent()) return;
            setChecked(false);
            setPayInvoiceModal(false);
            let parsed: any;
            try { parsed = JSON.parse(e.message); } catch { /* Transport errors may not have a JSON body. */ }
            if (parsed?.data?.paymentOutcome === 'unknown' || !e?.status || e.status >= 500) {
                setPaymentConfirmationPending(true);
                setQuoteNotice('');
                return;
            }
            try {
                if (e?.status === 409 && parsed?.data?.reason === 'invoice_payment_quote_changed') {
                    setChecked(false);
                    setPayInvoiceModal(false);
                    setQuoteNotice('The payment total changed. Please review it and authorize payment again.');
                    getData();
                    return;
                }
                const message: string = parsed?.message ?? 'We were unable to process your payment.';
                // If backend signals contact support (403 + CONTACT_SUPPORT message), open dedicated modal
                if (e?.status === 403 && typeof message === 'string' && message.toLowerCase().includes('contact tempstars support')) {
                    setContactSupportOpen(true);
                    return;
                }
                setErrorTitle('Error');
                setError(`Error processing payment: ${message}`);
            } catch {
                setErrorTitle('Error');
                setError('Error processing payment. Please try again later.');
            }
        }
    });

    const payInvoiceHandler = (e: any) => {
        e.preventDefault()
        if (!isCurrent()) return;
        if (!quote || paymentConfirmationPending || getInvoiceForJobLoading || payInvoiceLoading) return;
        if (!checked) {
            setErrorTitle('Cannot Proceed')
            setError('Please agree to Pay Now payment authorization to process payment.')
        } else {
            setPayInvoiceModal(true)
        }
    }

    const alreadyPaidHandler = (e: any) => {
        e.preventDefault()
        if (!isCurrent()) return;
        setAlreadyPaidModal(true)
    }

    const onGoPaymentInfoPageClick = (e: any) => {
        if (!isCurrent()) return;
        setAlreadyPaidModal(false)
        setError('')
        if (job) {
            const locId = job.location?.id ?? job.locationId;
            history.push({ pathname: 'paymentInfo', state: { locationId: locId, jobId, fromDentistInvoices: true, paymentPurpose: 'native' } })
        }
    }

    function toDayJS(date?: DateString, time?: TimeString | null): dayjs.Dayjs | null {
        if (!date || !time) {
            return null;
        }
        return dayjs(date + ' ' + time, 'YYYY-MM-DD h:mm a');
    }

    const shift = job?.shifts?.[0];
    const startTime = toDayJS(job?.startDate, shift?.actualStart)?.format("h:mma")
    const endTime = toDayJS(job?.startDate, shift?.actualEnd)?.format("h:mma")
    const paymentStatusCopy = getTalentPaymentStatusCopy(
        talentPaymentStatus,
        job?.invoice?.talentPaymentMethod,
        job?.invoice?.officePaymentStatus
            || (job?.invoice?.dentistMarkedPaid ? 'paid' : null),
    );

    if (loading) {
        return <FixedLoader loading={true} />;
    }

    if (error) {
        return (
            <BasicContainer>
                <SimpleModal isOpen={true}>
                    <ErrorModal
                        message={error}
                        title={errorTitle}
                        onCloseButtonClick={goHome}
                        onGoPaymentInfoPageClick={onGoPaymentInfoPageClick}
                    />
                </SimpleModal>
            </BasicContainer>
        );
    }

    if (!job) {
        return <></>
    }

    return (
        <>
            <TopBarRedesigned
                onBack={goHome}
                title={'Invoice Details'}
            />

            <BasicContainer>
                <SimpleModal isOpen={contactSupportOpen}>
                  <div className={styles.titleContainer} style={{marginBottom: '0'}}>
                    <div className={styles.popupTitle}>{"We Can't Process This Payment"}</div>
                    <CrossButtonBlack className={clsx('no-fastclick', styles.crossButtonDark)} onClick={() => setContactSupportOpen(false)} />
                  </div>
            
                  <div className={styles.description}>
                    <p>It looks like there was a problem with payment. Please contact our Customer Support at <a href="mailto:help@tempstars.com">help@tempstars.com</a> and they'll be happy to assist!</p>
                  </div>
            
                  {/*
                  <StyledButton
                    variant="contained"
                    size="large"
                    onClick={() => window.open('https://support.tempstars.com/hc/en-us/requests/new', '_blank')}
                    className={clsx(styles.okButton)}
                  >
                    Contact Support
                  </StyledButton> 
                  */}
            
                  <StyledButton
                    variant="outlined"
                    size="large"
                    onClick={() => setContactSupportOpen(false)}
                    className={clsx(styles.okButton)}
                  >
                    Close
                  </StyledButton>
                </SimpleModal>
                <SimpleModal isOpen={!!error.length}>
                    <ErrorModal
                        message={error}
                        title={errorTitle}
                        onCloseButtonClick={() => setError('')}
                        onGoPaymentInfoPageClick={onGoPaymentInfoPageClick}
                    />
                </SimpleModal>

                <SimpleModal isOpen={alreadyPaidModal} >
                    <InvoiceAlreadyPaidModal
                        job={job}
                        isCurrent={isCurrent}
                        onCloseButtonClick={() => setAlreadyPaidModal(false)}
                        onPaid={() => {
                            if (!isCurrent()) return;
                            setAlreadyPaid(true)
                            setAlreadyPaidModal(false)
                            getData()
                        }}
                    />
                </SimpleModal>

                <SimpleModal isOpen={payInvoiceModal} >
                    <PayInvoiceModal
                        job={job}
                        onPayInvoice={() => {
                            if (!isCurrent()) return;
                            setPayInvoiceModal(false)
                            getInvoiceForJob(job.id)
                        }}
                        onCloseButtonClick={() => setPayInvoiceModal(false)}
                    />
                </SimpleModal>

                <AddressSection>
                    <div>
                        <SectionTitle className="title">{formatTalentDisplayName(job.hygienist.firstName, job.hygienist.lastName, job.hygienist.preferredName)}</SectionTitle>
                        <div className="subtitle">{job.hygienist.address}</div>
                        <div className="subtitle">{job.hygienist.city}, {job.hygienist.province}</div>
                        <div className="subtitle">{job.hygienist.postalCode}</div>
                    </div>
                    <div>
                        <SectionTitle className="title">Invoice #{job.id}</SectionTitle>
                        <div className="subtitle">{dayjs(job.invoice.sentOn).format('dddd, MMM D')}</div>
                    </div>
                </AddressSection>

                <DateSection>
                    <div className="calendar-date">
                        <CalendarTodayOutlined fontSize="medium"/>
                        {dayjs(job.startDate).format('dddd, MMM D')}
                    </div>
                    <div>{startTime} - {endTime}</div>
                </DateSection>

                <BillingSection>
                    <SectionTitle>
                        <AccessTimeOutlined fontSize="medium"/> Time & Billing Details
                    </SectionTitle>
                    
                    <BillingRow>
                        <div>Total Time</div>
                        <div>{formatInvoiceDuration(job.invoice.totalHours)}hrs</div>
                    </BillingRow>
                    {job.invoice.lunchHours != null ? (
                        <>
                            <BillingRow>
                                <div>Unpaid Lunch (Unpaid)</div>
                                <div>{formatInvoiceDuration(job.invoice.lunchHours)}hrs</div>
                            </BillingRow>
                            {job.invoice.downtimeHours != null && (
                                <BillingRow>
                                    <div>Downtime ({job.shifts[0].gapUnpaid ? 'Unpaid' : 'Paid'})</div>
                                    <div>{formatInvoiceDuration(job.invoice.downtimeHours)}hrs</div>
                                </BillingRow>
                            )}
                        </>
                    ) : (
                        <BillingRow>
                            <div>Unpaid Time</div>
                            <div>{formatInvoiceDuration(job.invoice.totalUnpaidHours)}hrs</div>
                        </BillingRow>
                    )}
                    <BillingRow className="billable-duration-row">
                        <div>Billable Time</div>
                        <div data-testid="office-invoice-billable-duration">{formatBillableDuration(job.invoice?.totalBillableHours)}</div>
                    </BillingRow>
                    <BillingRow>
                        <div>Hourly Rate</div>
                        <div>${job.invoice.hourlyRate}/hr</div>
                    </BillingRow>
                    <BillingRow>
                        <div>Travel Allowance</div>
                        {/* T2-7537: dollars with cents ("$0.00", "$724.50"), like the Pay Now amounts. */}
                        <div data-testid="invoice-travel-allowance">
                            ${formatInvoiceCents(Math.round(Number(job.invoice?.travelAllowance ?? 0) * 100))}
                        </div>
                    </BillingRow>
                    <BillingRow className="grand-total">
                        <div>Grand Total</div>
                        <div data-testid="invoice-grand-total">
                            ${formatInvoiceCents(Math.round(Number(job.invoice.totalInvoiceAmt) * 100))}
                        </div>
                    </BillingRow>
                </BillingSection>

                <PaymentSection>
                    <SectionTitle>
                        <PaymentOutlined fontSize="medium" /> Payment Information
                    </SectionTitle>
                    <PaymentInfo>
                        <div>Payment Terms:</div>
                        <div>You agreed to pay {job.hygienist.firstName} <span className="payment-terms">{getPaymentTerms(job?.paymentTerms)}</span></div>
                    </PaymentInfo>
                    <PaymentInfo>
                        <div>Expected Payment Date:</div>
                        <div>{dayjs(job.invoice.sentOn).add(job?.paymentTerms, 'day').format('dddd, MMM D')}</div>
                    </PaymentInfo>
                    {shouldShowPostShiftChatNote(job) && (
                        <PaymentNote style={{ marginTop: 12 }}>
                            {CHAT_POST_SHIFT_NOTE}
                        </PaymentNote>
                    )}
                </PaymentSection>

                {alreadyPaid ? (
                    paymentStatusCopy ? (
                        <PaymentStatusBanner $tone={paymentStatusCopy.tone} data-testid="invoice-payment-status">
                            <h2>{paymentStatusCopy.title}</h2>
                            <p>{paymentStatusCopy.detail}</p>
                        </PaymentStatusBanner>
                    ) : (
                        <AlreadyPaidBanner>
                            <h2>✓ Invoice Paid</h2>
                            <InvoicePaymentAudit invoice={job.invoice} audience="office" talentName={formatTalentDisplayName(job.hygienist.firstName, job.hygienist.lastName, job.hygienist.preferredName)} />
                        </AlreadyPaidBanner>
                    )
                ) : isPayNowAvailable ? (
                    <>
                        <PaymentNote>
                            Most offices use TempStars' Pay Now to pay their invoices - it's fast & easy, but optional. If you have a preferred way to pay temp workers, that's fine. When you use Pay Now, we'll forward {job.hygienist.firstName}'s payment to them within 24hrs of receiving it.
                        </PaymentNote>

                        {paymentConfirmationPending && <PaymentStatusBanner $tone="attention" data-testid="invoice-payment-unconfirmed">
                            <h2>Payment confirmation pending</h2>
                            <p>We could not confirm the payment result. Please do not submit another payment or pay this invoice by another method. Refresh its status below, or contact TempStars Support for help.</p>
                            <StyledButton onClick={() => { if (isCurrent()) { setLoading(true); getData(); } }}>Refresh payment status</StyledButton>
                        </PaymentStatusBanner>}
                        {quoteNotice && <PaymentNote role="status">{quoteNotice}</PaymentNote>}
                        {quote ? <StyledCheckboxInput
                            name={'payment'}
                            disabled={paymentConfirmationPending}
                            label={quote.feeWaived ?
                                `I authorize TempStars to process $${invoiceAmount} to my credit card, to settle ${job.hygienist.firstName}'s invoice for services provided on ${dayjs(job.startDate).format('dddd, MMM D')}, for a total of $${invoiceAmtWithFee} ${quote.currency.toUpperCase()}. As a convenient payment intermediary, TempStars will forward $${invoiceAmount} to ${job.hygienist.firstName} ${job.hygienist.lastName} on your behalf.` :
                                `I authorize TempStars to process $${invoiceAmount} + ${quote.feeBasisPoints / 100}% payment processing fee to my credit card, to settle ${job.hygienist.firstName}'s invoice for services provided on ${dayjs(job.startDate).format('dddd, MMM D')}, for a total of $${invoiceAmtWithFee} ${quote.currency.toUpperCase()}. As a convenient payment intermediary, TempStars will forward $${invoiceAmount} to ${job.hygienist.firstName} ${job.hygienist.lastName} on your behalf, and retain the ${quote.feeBasisPoints / 100}% fee to cover our transaction costs.`
                            }
                            checked={checked}
                            onChange={() => { if (isCurrent()) { setChecked(!checked); setQuoteNotice(''); } }}
                        /> : <PaymentNote role="status">Could not load the payment total. Please close and reopen this invoice before using Pay Now.</PaymentNote>}

                        <ButtonsContainer>
                            <PayNowButton
                                data-testid="invoice-pay-now"
                                disabled={alreadyPaidModal || alreadyPaid || !quote || paymentConfirmationPending || getInvoiceForJobLoading || payInvoiceLoading}
                                onClick={payInvoiceHandler}
                            >
                                Pay Now
                            </PayNowButton>

                            <AlreadyPaidButton
                                disabled={paymentConfirmationPending}
                                onClick={alreadyPaidHandler}
                            >
                                <HelpOutlineOutlined fontSize="small" />
                                Have you already paid this invoice?
                            </AlreadyPaidButton>
                        </ButtonsContainer>
                    </>
                ) : (
                    <MarkAsPaidSection>
                        <p className="mark-paid-note">
                            Pay Now is not available for this invoice because {job.hygienist.firstName} has not set up direct deposit. If you've already paid {job.hygienist.firstName} by check, e-transfer, or another method, you can mark this invoice as paid to clear the alert.
                        </p>
                        <button
                            className="mark-paid-button"
                            onClick={alreadyPaidHandler}
                        >
                            <CheckCircleOutline fontSize="small" />
                            Mark as Paid
                        </button>
                    </MarkAsPaidSection>
                )}
                {Number(job.paymentMethod) !== 1 && <InvoiceInformationRequest invoiceId={Number(job.invoice.id)}
                    settled={alreadyPaid || officeInvoiceIsPaid(job.invoice) || paymentConfirmationPending} />}
                <InvoiceDisputeOffice invoiceId={Number(job.invoice.id)}
                    settled={alreadyPaid || officeInvoiceIsPaid(job.invoice) || paymentConfirmationPending}
                    onRefresh={getData} />
            </BasicContainer>
            <FixedLoader loading={getInvoiceForJobLoading || payInvoiceLoading}/>
        </>
    );
};

export default InvoicePage;
